import type { PermissionResult } from './types';

/** Error classification mapping from DOMException names to structured results */
const ERROR_MAP: Record<string, Pick<PermissionResult, 'error' | 'message'>> = {
  NotAllowedError: {
    error: 'not_allowed',
    message: 'Microphone access denied. Enable it in browser settings.',
  },
  NotFoundError: {
    error: 'not_found',
    message: 'No microphone found. Connect a microphone.',
  },
  NotReadableError: {
    error: 'not_readable',
    message: 'Microphone is in use by another application.',
  },
  OverconstrainedError: {
    error: 'overconstrained',
    message: 'Selected microphone unavailable. Using default.',
  },
};

/**
 * Manages microphone permission lifecycle with structured error classification.
 * Reuses the "throwaway getUserMedia + track release" pattern from RecordingContext.
 */
export class PermissionManager {
  private currentState: PermissionResult = { granted: false };

  /** Get the current permission state */
  getState(): PermissionResult {
    return this.currentState;
  }

  /**
   * Check the current permission state without prompting.
   * Uses navigator.permissions.query() when available, which avoids
   * triggering a browser permission prompt.
   */
  async checkPermission(): Promise<PermissionResult> {
    // Try permissions.query() first (supported in Chrome, Firefox, Edge)
    try {
      if (navigator.permissions?.query) {
        const status = await navigator.permissions.query({
          name: 'microphone' as PermissionName,
        });

        if (status.state === 'granted') {
          this.currentState = { granted: true };
          return this.currentState;
        }

        if (status.state === 'denied') {
          this.currentState = {
            granted: false,
            error: 'not_allowed',
            message: 'Microphone access denied. Enable it in browser settings.',
          };
          return this.currentState;
        }

        // 'prompt' state — permission not yet decided, don't trigger a prompt
        return this.currentState;
      }
    } catch {
      // permissions.query not supported (Safari) or failed — fall through
    }

    // Can't determine permission state without prompting
    return this.currentState;
  }

  /**
   * Request microphone permission by performing a throwaway getUserMedia call.
   * This will trigger the browser permission prompt if not already granted.
   */
  async requestPermission(): Promise<PermissionResult> {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Release the stream immediately — we only needed it for the permission prompt
      stream.getTracks().forEach(t => t.stop());

      this.currentState = { granted: true };
      return this.currentState;
    } catch (error: unknown) {
      this.currentState = this.classifyError(error);
      return this.currentState;
    }
  }

  /**
   * Classify a getUserMedia error into a structured PermissionResult.
   * Maps DOMException names to user-friendly error types and messages.
   */
  classifyError(error: unknown): PermissionResult {
    if (error instanceof DOMException || (error instanceof Error && 'name' in error)) {
      const name = (error as DOMException).name;
      const mapped = ERROR_MAP[name];
      if (mapped) {
        return { granted: false, ...mapped };
      }
    }

    const message =
      error instanceof Error
        ? `Error accessing microphone: ${error.message}`
        : 'Error accessing microphone: Unknown error';

    return { granted: false, error: 'unknown', message };
  }
}
